/**
 * 全局确认弹窗 —— 替代阻塞、风格割裂的 window.confirm,视觉跟应用主题一致。
 *
 * 用法:
 *   import { useConfirm } from '@/composables/useConfirm'
 *   const { confirm } = useConfirm()
 *   const ok = await confirm({
 *     title: '归档商品',
 *     message: '确定归档「真丝雪纺」?\n\n历史保留,可随时恢复。',
 *     confirmText: '归档',
 *     danger: true,
 *   })
 *   if (!ok) return
 *
 * 实现:模块级 reactive 单实例 + 容器组件 (ConfirmDialog.vue) 在 AppLayout 挂一次,
 * 跟 useToast 同形。confirm() 返回 Promise<boolean>:点确认 resolve(true) /
 * 取消(按钮 / 遮罩 / Esc) resolve(false)。
 */
import { reactive } from 'vue'

export interface ConfirmOptions {
  /** 标题,默认「确认」 */
  title?: string
  /** 正文,支持 \n 换行(容器 white-space: pre-line) */
  message: string
  /** 确认按钮文案,默认「确定」 */
  confirmText?: string
  /** 取消按钮文案,默认「取消」 */
  cancelText?: string
  /** 危险操作(删除 / 归档 / 撤销)→ 确认按钮标红 */
  danger?: boolean
}

interface ConfirmState {
  open: boolean
  title: string
  message: string
  confirmText: string
  cancelText: string
  danger: boolean
}

const state: ConfirmState = reactive({
  open: false,
  title: '确认',
  message: '',
  confirmText: '确定',
  cancelText: '取消',
  danger: false
})

let resolver: ((ok: boolean) => void) | null = null

function confirm(opts: ConfirmOptions): Promise<boolean> {
  // 上一个还没结算就又弹(理论上不该发生)→ 先把旧的当取消结算,避免 await 永远挂着。
  if (resolver) {
    resolver(false)
    resolver = null
  }
  state.title = opts.title ?? '确认'
  state.message = opts.message
  state.confirmText = opts.confirmText ?? '确定'
  state.cancelText = opts.cancelText ?? '取消'
  state.danger = opts.danger ?? false
  state.open = true
  return new Promise<boolean>((resolve) => {
    resolver = resolve
  })
}

/** 容器组件点确认/取消时调用,结算当前 promise。 */
function settle(ok: boolean) {
  if (!state.open) return
  state.open = false
  const r = resolver
  resolver = null
  r?.(ok)
}

export function useConfirm() {
  return {
    /** 内部 reactive state,容器组件 ConfirmDialog 用 */
    state,
    /** 弹确认框,返回 Promise<boolean> */
    confirm,
    /** 容器组件点确认(true)/取消(false)时调用 */
    _settle: settle
  }
}
